// @flow
import React from "react";
import { padStart } from "lodash";
import DOMPurify from "dompurify";
import { isToday, isBefore } from "date-fns";

import type { RaceStatusEnum } from "@tvg/types/Race";

import {
  formatPostTime,
  formatPeriod,
  formatRaceDate
} from "@tvg/formatter/dates";

import PlainMTP, {
  Container,
  Timer,
  Description,
  CondensedDescription,
  TimerText
} from "./styled-components";

const showFullDate = (
  status: RaceStatusEnum,
  mtp: number,
  showRaceDate: boolean,
  mtpThreshold = 60
): boolean =>
  status !== "SK" && status !== "RO" && mtp > mtpThreshold && showRaceDate;

const showFullDateFuture = (
  status: RaceStatusEnum,
  mtp: number,
  postTime: string,
  mtpThreshold = 60
): boolean => {
  const datePostTime = new Date(postTime);
  return (
    status !== "SK" &&
    status !== "RO" &&
    mtp > mtpThreshold &&
    !isToday(datePostTime) &&
    isBefore(new Date(), datePostTime)
  );
};
export const DefaultMTPText = {
  resulted: "Resulted",
  raceOff: "Race Off",
  upcoming: "MINUTES<br/>TILL START",
  upcomingSmall: "MIN<br/>TILL START",
  raceInfoAbove60: "RACE START",
  raceInfo: "TILL RACE OFF"
};

export type MTPTextTime = {
  resulted: string,
  raceOff: string,
  upcoming: string,
  upcomingSmall: string,
  raceInfoAbove60: string,
  raceInfo: string
};

type MTPMode = "dark" | "light" | "default" | "derby";

type Props = {
  /**
   * MTP value ex: 3
   */
  mtp: number,
  /**
   * PostTime string value ex: 2017-10-29T09:28:00.000Z
   */
  postTime: string,
  /**
   * Status of the race
   */
  status: RaceStatusEnum,
  /**
   * Increase size o text
   */
  size: number,
  /**
   * Whenever the mtp shows the race date when mtp > 60
   */
  showRaceDate: boolean,
  /**
   * Is full name for off state
   */
  isRaceOff: boolean,
  /*+
   * Is new layout of MTP
   */
  isNewLayout: boolean,
  /*+
   * Is data qa label
   */
  qaLabel: string,
  /*+
   * Button Text
   */
  textTime: MTPTextTime,
  /*
   * Show MTP in small layout
   */
  isSmall: boolean,
  /*
   * Change color by mode
   */
  mode: MTPMode,
  /*
   * Show description inline
   */
  inline: boolean,
  /*
   * Force to show description inline
   */
  forceDescription: boolean,
  /*
   * Show description inline
   */
  showDescription: boolean,
  /*
   * Show post time
   */
  showPostTime: boolean,
  /*
   * Show small text variable `typeSmall` on capi
   */
  isSmallText: boolean,
  /*
   * Show description in condensed font
   */
  condensedDescription?: boolean,
  /*
   * Keeps the default color for the text regardless of remaining time
   */
  keepDefaultColor?: boolean,
  /*
   * Is future bets tab active
   */
  isFutureBetsTab?: boolean
};

type MtpStatus = "raceOff" | "result" | "fullDate" | "closest" | "distance";

type MTPProps = {
  mtpStatus: MtpStatus,
  qaLabel: string,
  timer: string,
  description: ?string,
  mode: MTPMode,
  inline: boolean,
  forceDescription: boolean,
  showDescription: boolean,
  condensedDescription?: boolean,
  keepDefaultColor?: boolean,
  isFutureBetsTab?: boolean
};

export const getMTPStatus = (status: RaceStatusEnum, mtp: number = 0) => {
  let mtpStatus = "distance";

  if (status === "SK") {
    mtpStatus = "raceOff";
  } else if (status === "RO") {
    mtpStatus = "result";
  } else if (mtp > 60) {
    mtpStatus = "fullDate";
  } else if (mtp <= 2) {
    mtpStatus = "closest";
  }

  return mtpStatus;
};

const renderBig = ({
  qaLabel,
  mtpStatus,
  timer,
  description,
  mode,
  showDescription,
  keepDefaultColor
}: MTPProps) => {
  return (
    <Container
      data-qa-label={qaLabel}
      mtpStatus={mtpStatus}
      mode={mode}
      keepDefaultColor={keepDefaultColor}
    >
      <Timer mtpStatus={mtpStatus}>{timer}</Timer>
      {showDescription && description && (
        <Description
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
        />
      )}
    </Container>
  );
};

export const removeHTML = (text: string) => {
  const htmlRegex = new RegExp("(<([^>]+)>)", "ig");
  const spaceRegex = new RegExp("\\s\\s+", "g");
  return text.replace(htmlRegex, " ").replace(spaceRegex, " ").trim();
};

const renderSmall = ({
  qaLabel,
  mtpStatus,
  timer,
  description,
  mode,
  inline,
  forceDescription,
  showDescription,
  condensedDescription,
  keepDefaultColor,
  isFutureBetsTab
}: MTPProps) => (
  <Container
    data-qa-label={qaLabel}
    mtpStatus={mtpStatus}
    isSmall
    mode={mode}
    inline={inline}
    condensedDescription={condensedDescription}
    keepDefaultColor={keepDefaultColor}
  >
    <Timer
      mtpStatus={mtpStatus}
      isSmall
      inline={inline}
      showDescription={showDescription}
      isFutureBetsTab={isFutureBetsTab}
    >
      {timer}
    </Timer>
    {condensedDescription ? (
      <CondensedDescription>
        {showDescription &&
          (inline || forceDescription) &&
          removeHTML(description || "")}
      </CondensedDescription>
    ) : (
      <TimerText>
        {showDescription &&
          (inline || forceDescription) &&
          removeHTML(description || "")}
      </TimerText>
    )}

    {showDescription && description && !inline && (
      <Description
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
        isSmall
      />
    )}
  </Container>
);

const newLayout = ({
  mtp,
  postTime,
  status,
  qaLabel,
  textTime,
  isSmall,
  mode,
  inline,
  forceDescription,
  showDescription,
  showPostTime,
  isSmallText,
  condensedDescription,
  keepDefaultColor,
  isFutureBetsTab
}: Props) => {
  let generalProps = {
    mode,
    inline,
    showDescription,
    showPostTime,
    description: undefined,
    forceDescription,
    condensedDescription,
    keepDefaultColor
  };

  switch (status) {
    case "SK":
      generalProps = {
        ...generalProps,
        mtpStatus: getMTPStatus(status, mtp),
        qaLabel: `${qaLabel}RaceOff`,
        timer: textTime.raceOff
      };
      break;
    case "RO":
      generalProps = {
        ...generalProps,
        mtpStatus: getMTPStatus(status, mtp),
        qaLabel: `${qaLabel}Result`,
        timer: textTime.resulted
      };
      break;
    default:
      if (isFutureBetsTab && showFullDateFuture(status, mtp, postTime)) {
        generalProps = {
          ...generalProps,
          isFutureBetsTab,
          mtpStatus: getMTPStatus(status, mtp),
          qaLabel: `${qaLabel}FullDate-Futures`,
          timer: `${formatPostTime(postTime)} ${formatPeriod(
            postTime
          )} - ${formatRaceDate(postTime)}`
        };
      } else if (mtp > 60) {
        let timer = formatPostTime(postTime, false);
        let description = formatPeriod(postTime);

        if (!showDescription || forceDescription || (isSmall && !inline)) {
          timer = `${timer} ${description}`;
          description = textTime.raceInfoAbove60;
        }

        generalProps = {
          ...generalProps,
          mtpStatus: getMTPStatus(status, mtp),
          qaLabel: `${qaLabel}FullDate`,
          timer,
          description
        };
      } else {
        const mtpStatus = getMTPStatus(status, mtp);

        let timer = padStart(`${mtp}`, 2, "0");
        let description = isSmallText
          ? textTime.upcomingSmall
          : textTime.upcoming;

        if (!showDescription || forceDescription || (isSmall && !inline)) {
          timer = `${timer} min`;
          description = textTime.raceInfo;
        }

        generalProps = {
          ...generalProps,
          mtpStatus,
          qaLabel: `${qaLabel}${mtpStatus}`,
          timer,
          description
        };
      }
  }

  return isSmall ? renderSmall(generalProps) : renderBig(generalProps);
};

const oldLayout = ({
  mtp,
  postTime,
  status,
  size = 12,
  showRaceDate,
  isRaceOff = false
}: Props) => {
  const generalProps = {
    dateTime: { postTime },
    size
  };

  switch (status) {
    case "SK":
      return (
        <PlainMTP
          {...generalProps}
          data-qa-label="mtpRaceOff"
          mtpStatus={status}
        >
          {isRaceOff ? "RACE OFF" : "OFF"}
        </PlainMTP>
      );
    case "RO":
      return (
        <PlainMTP
          {...generalProps}
          data-qa-label="mtpResult"
          mtpStatus={status}
        >
          RESULT
        </PlainMTP>
      );
    default: {
      let mtpFormated = `${formatPostTime(
        postTime,
        showRaceDate
      )}${formatPeriod(postTime)}`;
      let mtpLevel = mtp <= 10 ? "below10" : "above10";

      if (showFullDate(status, mtp, showRaceDate)) {
        mtpLevel = "fullDate";
        mtpFormated = `${formatPostTime(postTime, showRaceDate)}`;
      }

      return (
        <PlainMTP
          {...generalProps}
          data-qa-label={`mtp${mtpLevel}`}
          mtpStatus={mtpLevel}
        >
          {mtp > 60 ? `${mtpFormated}` : `${mtp}MIN`}
        </PlainMTP>
      );
    }
  }
};

const MTP = (props: Props) =>
  props.isNewLayout ? newLayout(props) : oldLayout(props);

MTP.defaultProps = {
  mtp: 0,
  postTime: new Date().toISOString(),
  status: "O",
  isRaceOff: false,
  isNewLayout: false,
  showRaceDate: false,
  size: 12,
  qaLabel: "mtp",
  textTime: DefaultMTPText,
  isSmall: false,
  mode: "default",
  inline: false,
  forceDescription: false,
  showDescription: true,
  showPostTime: false,
  isSmallText: false,
  condensedDescription: false,
  keepDefaultColor: false,
  isFutureBetsTab: false
};

export default MTP;
